/**
 * The ring an incoming call makes. Two tones from the Web Audio API in
 * the New Zealand cadence (0.4s on, 0.2s off, 0.4s on, 2s off), so there
 * is no audio asset to ship and nothing to fetch when the phone rings.
 *
 * Browsers refuse to start audio until the page has had a click; a phone
 * the person has opened has had one. Where it is refused anyway, or
 * where there is no AudioContext at all (tests), the ring is silent and
 * the screen still says a call is coming in.
 */
export interface Ringer {
  start(): void;
  stop(): void;
}

const CADENCE_MS: Array<[on: boolean, ms: number]> = [
  [true, 400],
  [false, 200],
  [true, 400],
  [false, 2000],
];

export function createRinger(): Ringer {
  let ctx: AudioContext | null = null;
  let gain: GainNode | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let ringing = false;

  function ensure(): boolean {
    if (ctx) return true;
    const Ctor = typeof window !== "undefined" ? window.AudioContext : undefined;
    if (!Ctor) return false;
    try {
      ctx = new Ctor();
      gain = ctx.createGain();
      gain.gain.value = 0;
      gain.connect(ctx.destination);
      for (const hz of [400, 450]) {
        const osc = ctx.createOscillator();
        osc.frequency.value = hz;
        osc.connect(gain);
        osc.start();
      }
      return true;
    } catch {
      ctx = null;
      gain = null;
      return false;
    }
  }

  function step(i: number) {
    if (!ringing || !gain) return;
    const [on, ms] = CADENCE_MS[i % CADENCE_MS.length];
    gain.gain.value = on ? 0.15 : 0;
    timer = setTimeout(() => step(i + 1), ms);
  }

  return {
    start() {
      if (ringing) return;
      ringing = true;
      if (!ensure()) return;
      ctx?.resume().catch(() => {});
      step(0);
    },
    stop() {
      ringing = false;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (gain) gain.gain.value = 0;
    },
  };
}
